import type { ReceiptConfidence } from "@/api/types";
import { Icon } from "@/components/Icon";
import { formatReceiptDate, receiptDateBounds } from "@/lib/receiptDate";
import "./ReceiptConfidenceNotice.css";

interface ReceiptConfidenceNoticeProps {
  confidence: ReceiptConfidence;
  /**
   * The receipt's own date, when one was read and believed. Shown because the entry is
   * BOOKED on it — the wall's feed labels rows by `created_at`, so a backdated entry reads
   * "just now" there and the reader would otherwise have no way to tell. Silent backdating
   * is its own hazard; this is what stops it being silent.
   */
  date?: string | null;
  /**
   * Hand this in and the booking day becomes EDITABLE — the sentence turns into the
   * control. Leave it out and the notice states the day, as it always has.
   *
   * WHY THE CALLER DECIDES. This component's header rule is that the copy describes the
   * SCAN and never the next action, because the two screens that scan do different things
   * afterwards: `/entry` puts the fields in front of the visitor to correct, and the
   * dashboard's capture box files the entry straight away. On the second one there is
   * nothing left to correct — the entry already exists — so an input there would be a
   * control that changes nothing. The screen that offers correction passes a handler; the
   * one that does not, does not.
   */
  onDateChange?: (iso: string) => void;
}

/**
 * How sure the analyzer was, said out loud.
 *
 * <p>
 * `ReceiptAnalysis.confidence` shipped in the contract carrying its own requirement —
 * <i>"Always shown — a wrong answer presented as certain is worse at a booth than an
 * uncertain one the visitor can correct"</i> — and nothing rendered it. The API answered
 * High, Medium or Low and every scan looked identical, so a clean read and a guess were
 * indistinguishable to the person holding the phone.
 * </p>
 *
 * <p>
 * THE COPY DESCRIBES THE SCAN, NEVER THE NEXT ACTION. Both screens that scan do something
 * different afterwards: `/entry` puts the fields in front of the visitor to correct, and
 * `/capture` files the entry straight away. "Check this before you save" would be a lie on
 * one of them, so the notice states what it knows — how legible the photo was — and leaves
 * the surrounding screen to say what happens next.
 * </p>
 *
 * <p>
 * NOT AN ERROR STATE, even at Low. Nothing failed: the analyzer read a bad photo and said
 * so, which is the product working. `role="status"` (polite), never `role="alert"`, and no
 * red — the same call `PaymentAnomalyNotice` makes, for the same reason. Gold is used as a
 * fill only and never as a text colour on white, per docs/brand/README.md.
 * </p>
 *
 * <p>
 * The level is carried in WORDS as well as tone, so it survives being read aloud, printed,
 * or seen by somebody who cannot separate the two backgrounds.
 * </p>
 */
export function ReceiptConfidenceNotice({
  confidence,
  date,
  onDateChange,
}: ReceiptConfidenceNoticeProps) {
  const { headline, detail, icon, tone } = COPY[confidence];
  const bounds = receiptDateBounds();

  return (
    <div className={`scan-confidence scan-confidence--${tone}`} role="status">
      <span className="scan-confidence__icon" aria-hidden="true">
        <Icon name={icon} size={16} strokeWidth={2.2} />
      </span>
      <span className="scan-confidence__text">
        <strong className="scan-confidence__headline">{headline}</strong>{" "}
        <span className="scan-confidence__detail">{detail}</span>
        {/*
          THE BOOKING DAY IS STATED IN BOTH DIRECTIONS, NOT JUST THE HAPPY ONE.

          MEASURED 2026-09-07, live cortex-vl, through the booth phone. An 8-degree tilt —
          a phone held slightly crooked over a counter — came back "High", amount right, and
          the date read Aug 6 against a receipt printed Aug 26. A low-resolution frame came
          back with a 2023 date, which `isPlausibleReceiptDate` correctly dropped. The entry
          then booked TODAY, proven by reading entry.occurred_on out of the database, and
          NOTHING ON THE SCREEN SAID SO — while the notice was at that moment telling the
          operator "worth checking before you save".

          So the product volunteered the booking day exactly when it was confident, and went
          quiet exactly when it was not. That is backwards: the uncertain read is the one
          where the booking day is worth knowing, and it was the only one that stayed silent.

          Neither branch is a next-action instruction of the kind this component's header
          rules out — both state where the money lands, which is a fact about THIS entry and
          is invisible everywhere else. The wall's feed labels rows by `created_at`, so a
          backdated row still reads "just now" there, and a booked-today row is
          indistinguishable from a correctly-dated one.
        */}
        {" "}
        {onDateChange ? (
          /*
            THE DATE IS THE ONE FIELD THE MODEL READS AND NOBODY COULD CORRECT.

            MEASURED TWICE ON THE LIVE MODEL. 2026-09-07: an 8-degree tilt — a phone held
            slightly crooked over a counter — read a receipt printed Aug 26 as Aug 6, amount
            correct, confidence HIGH. 2026-09-08, and this is the one that settles it: a
            pristine synthetic receipt printed 08/28/2026 came back 2026-08-08, FIVE TIMES
            OUT OF FIVE, amount correct and High every time. So it is not crooked
            photographs, and "take a better picture" is not the remedy.

            No server check can catch it. Right year, not in the future, non-zero amount —
            every floor in ImplausibleRead passes it, which that method's own remarks
            already say of this class: the correction path is what covers it. For the date
            there was no correction path. The phone offered four numbered steps — vendor,
            category, payment method, amount — all correctable, and then told the visitor
            "Read clearly … it will book on that day" about a day twenty days wrong.

            It is the SENTENCE that became the control, rather than a fifth step or a button:
            no new affordance to explain, the OS date wheel on both iOS and Android, and the
            claim and the thing it is a claim about are now the same element. `min`/`max`
            come from receiptDateBounds() so the picker cannot offer a day the plausibility
            check would then drop on the way out.

            Beyond the booth: a date error that crosses a quarter boundary lands in the
            wrong 941, and the year-end forms are the other half of this product.
          */
          <label className="scan-confidence__date scan-confidence__date--editable">
            <span>It will book on</span>{" "}
            <input
              type="date"
              value={date ?? bounds.max}
              min={bounds.min}
              max={bounds.max}
              aria-label="Date this receipt books on"
              onChange={(e) => {
                if (e.target.value) onDateChange(e.target.value);
              }}
            />
          </label>
        ) : (
          <span className="scan-confidence__date">
            {date
              ? `Dated ${formatReceiptDate(date)} — it will book on that day.`
              : "No date came off it — it will book today."}
          </span>
        )}
      </span>
    </div>
  );
}

/**
 * Deliberately a lookup rather than branching on `confidence` at each use site: the three
 * levels then have to stay exhaustive, and adding a fourth to the API becomes a compile
 * error here instead of a silently blank notice at the booth.
 */
const COPY: Record<
  ReceiptConfidence,
  { headline: string; detail: string; icon: "check" | "sparkle"; tone: "clear" | "unsure" }
> = {
  High: {
    headline: "Read clearly",
    detail: "Every field came straight off the photo.",
    icon: "check",
    tone: "clear",
  },
  Medium: {
    headline: "Mostly clear",
    detail: "Some of this was harder to read than the rest.",
    icon: "sparkle",
    tone: "unsure",
  },
  /*
    LOW DOES NOT MEAN BLURRY, and this line said it did.

    It read "The photo was blurry or cropped, so these may be wrong" — a statement of fact
    about the visitor's photograph, in the one place they are holding that photograph and can
    see it is fine.

    Low is not only the model's own uncertainty. `SparkReceiptAnalyzer.ImplausibleRead` caps
    it at Low when the DATE is in the future or over two years old, or the AMOUNT is zero —
    added 2026-09-07 because the live model answered "High" on ten reads including three that
    were wrong. A perfectly sharp photograph whose year the model misreads now lands here, and
    is told it was blurry.

    FOURTH INSTANCE OF ONE HABIT IN A DAY, and the only one that was mine: a scan taken before
    reference data loaded, a 500 from the analyzer, and a 500 at sign-in were each reported as
    the person's fault — their receipt, their receipt again, their wifi. This one arrived
    through a ceiling I added myself.

    The coaching is kept, because it is genuinely useful — blur is the only degradation
    measured to break the read — but it is offered as a possibility rather than asserted as
    the diagnosis.
  */
  Low: {
    headline: "Hard to read",
    detail: "Some of these may be wrong — worth checking before you save. If the photo was "
      + "blurry, another try usually fixes it.",
    icon: "sparkle",
    tone: "unsure",
  },
};
